import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Notification from "../../../components/Notification";
import { apiCall, formatDay } from "../../../utils/apiCall";
import { DeadlineNote, StatusBadge, STATUSES } from "./issueBits";

const FILTERS = [
    { value: "open", label: "Not closed" },
    { value: "overdue", label: "Overdue" },
    ...STATUSES.map((s) => ({ value: s, label: s })),
    { value: "all", label: "All" },
];

// The issue log: things that need to be addressed, who is responsible and by when.
// Admins and super admins see and manage all of it; other members see the issues they are responsible for.
function IssueList() {
    const [list, setList] = useState(null);
    const [types, setTypes] = useState([]);
    const [status, setStatus] = useState("open");
    const [type, setType] = useState("");
    const [mine, setMine] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchText.trim()); setPage(1); }, 350);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        apiCall("/issues/types").then(({ ok, data }) => ok && setTypes(data)).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({ status, page: String(page) });
            if (type) query.set("type", type);
            if (search) query.set("search", search);
            if (mine) query.set("mine", "1");
            const { ok, status: code, data } = await apiCall(`/issues?${query}`);
            if (code === 401) return navigate("/logout", { state: { from: location } });
            if (!ok) throw new Error(data?.message);
            setList(data);
            setError("");
        } catch (problem) {
            setError(problem.message || "The issue log could not be loaded.");
        } finally {
            setLoading(false);
        }
    }, [status, type, search, mine, page, navigate, location]);

    useEffect(() => {
        load();
    }, [load]);

    // Until the first reply, assume a member (the API decides what is shown either way).
    const canManage = Boolean(list?.can_manage);
    useEffect(() => {
        document.title = `${canManage ? "Issue Log" : "My Issues"} - CFCM Portal`;
    }, [canManage]);

    const choose = (setter) => (value) => { setter(value); setPage(1); };
    const rows = list?.data || [];

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <div>
                            <p className="title is-4">{canManage ? "Issue Log" : "My Issues"}</p>
                            <p className="cf-muted">
                                {canManage
                                    ? "Things that need to be addressed, who is responsible and by when. Members only see the issues they are responsible for."
                                    : "Things you have been made responsible for. Open one to report progress or to close it when it is done."}
                            </p>
                        </div>
                    </div>
                </div>
                {canManage && <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/issue-types" className="button primary-outline-button">Issue types</Link>
                        &nbsp;&nbsp;
                        <Link to="/dashboard/issues/new" className="button primary-bg">
                            <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; Log an issue
                        </Link>
                    </p>
                </div>}
            </nav>

            {error && <Notification type="danger" message={error} onClose={() => setError("")} />}

            {list && (
                <div className="cf-filter-tabs" role="group" aria-label="Which issues to show">
                    {FILTERS.map((f) => (
                        <button key={f.value} type="button" className={status === f.value ? "is-active" : ""} aria-pressed={status === f.value} onClick={() => choose(setStatus)(f.value)}>
                            {f.label}
                            {f.value === "open" && list.counts ? ` (${list.counts.open})` : ""}
                            {f.value === "overdue" && list.counts ? ` (${list.counts.overdue})` : ""}
                        </button>
                    ))}
                </div>
            )}

            <div className="columns is-variable is-2">
                <div className="column">
                    <div className="field">
                        <label className="label" htmlFor="issue-search">Search</label>
                        <input id="issue-search" className="input" type="search" placeholder="Title, description or person" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </div>
                </div>
                <div className="column is-narrow">
                    <div className="field">
                        <label className="label" htmlFor="issue-type">Type</label>
                        <div className="select">
                            <select id="issue-type" value={type} onChange={(e) => choose(setType)(e.target.value)}>
                                <option value="">All types</option>
                                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                <option value="other">Other (typed in)</option>
                            </select>
                        </div>
                    </div>
                </div>
                {canManage && <div className="column is-narrow">
                    <div className="field">
                        <label className="label is-hidden-mobile">&nbsp;</label>
                        <label className="checkbox mt-2">
                            <input type="checkbox" checked={mine} onChange={(e) => choose(setMine)(e.target.checked)} /> &nbsp;Mine only
                        </label>
                    </div>
                </div>}
            </div>

            {!list && loading && <p>Loading the issue log...</p>}

            {list && (
                <>
                    <div className="table-container" aria-busy={loading}>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>Issue</th>
                                    <th>Type</th>
                                    <th>Responsible</th>
                                    <th>Deadline</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? rows.map((issue) => (
                                    <tr key={issue.id} className={issue.overdue ? "cf-issue-row-late" : ""}>
                                        <td>
                                            <Link to={`/dashboard/issues/${issue.id}`} className="has-text-weight-semibold">{issue.title}</Link>
                                            <br /><span className="cf-muted is-size-7">Logged {formatDay(issue.created_at)}{issue.created_by ? ` by ${issue.created_by.name}` : ""}</span>
                                        </td>
                                        <td>{issue.type_name}</td>
                                        <td>
                                            {issue.assignees.length ? issue.assignees.map((a, i) => (
                                                <React.Fragment key={a.id}>
                                                    {i > 0 && ", "}
                                                    {a.user_id && canManage
                                                        ? <Link to={`/dashboard/members/${a.user_id}/details`} title={`Open ${a.name}'s profile`}>{a.name}</Link>
                                                        : a.name}
                                                </React.Fragment>
                                            )) : <span className="cf-muted">No one yet</span>}
                                        </td>
                                        <td className="cf-nowrap">
                                            {issue.deadline ? formatDay(issue.deadline) : ""}
                                            {issue.deadline && <br />}
                                            <span className="is-size-7"><DeadlineNote issue={issue} /></span>
                                        </td>
                                        <td className="cf-nowrap">
                                            <StatusBadge status={issue.status} />
                                            {issue.notifications_stopped && issue.status !== "Closed" && (
                                                <><br /><span className="cf-issue-muted-tag" title="No automatic reminders are sent for this issue"><i className="fa fa-bell-slash" aria-hidden="true"></i> Notifications stopped</span></>
                                            )}
                                        </td>
                                        <td className="has-text-right"><Link className="cf-row-link" to={`/dashboard/issues/${issue.id}`}>Open</Link></td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="6" className="has-text-centered">{status === "open" && !search && !type && !mine ? (canManage ? "Nothing open. Well done!" : "Nothing open is assigned to you.") : "No issues match."}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {list.last_page > 1 && (
                        <div className="cf-pager">
                            <button type="button" className="button is-small" disabled={list.current_page <= 1} onClick={() => setPage(list.current_page - 1)}>Previous</button>
                            <span>Page {list.current_page} of {list.last_page}</span>
                            <button type="button" className="button is-small" disabled={list.current_page >= list.last_page} onClick={() => setPage(list.current_page + 1)}>Next</button>
                        </div>
                    )}
                </>
            )}
        </main>
    );
}

export default IssueList;
